<script setup>
import { ref, computed } from 'vue'
import { RouterView, RouterLink, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import AppLogo from '@/components/common/AppLogo.vue'
import {
  HomeIcon,
  ShoppingBagIcon,
  PlayCircleIcon,
  CreditCardIcon,
  MegaphoneIcon,
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/vue/24/outline'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const sidebarOpen = ref(false)

const menuItems = [
  { name: 'Dashboard', icon: HomeIcon, route: 'dashboard' },
  { name: 'Meus Produtos', icon: ShoppingBagIcon, route: 'my-products' },
  { name: 'Vídeos', icon: PlayCircleIcon, route: 'my-videos' },
  { name: 'Pedidos', icon: ClipboardDocumentListIcon, route: 'orders' },
  { name: 'Financeiro', icon: CreditCardIcon, route: 'financial' },
  { name: 'Canal de Denúncias', icon: MegaphoneIcon, route: 'my-channel' },
  { name: 'Meu Perfil', icon: UserCircleIcon, route: 'profile' },
]

const currentRouteName = computed(() => route.name)

async function handleLogout() {
  await authStore.signOut()
  router.push({ name: 'home' })
}

function toggleSidebar() {
  sidebarOpen.value = !sidebarOpen.value
}

function closeSidebar() {
  sidebarOpen.value = false
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 lg:flex">
    <!-- Mobile sidebar backdrop -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
      @click="closeSidebar"
    ></div>

    <!-- Sidebar -->
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:relative lg:inset-auto lg:flex-shrink-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
    >
      <div class="h-full flex flex-col">
        <!-- Logo -->
        <div class="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          <RouterLink to="/" class="flex items-center gap-2">
            <AppLogo size="sm" />
          </RouterLink>
          <button
            class="lg:hidden p-2 text-gray-500 hover:text-gray-700"
            @click="closeSidebar"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- User info -->
        <div class="p-4 border-b border-gray-200">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span class="text-primary-700 font-semibold">
                {{ authStore.fullName?.charAt(0).toUpperCase() }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 truncate">
                {{ authStore.fullName }}
              </p>
              <p class="text-xs text-gray-500 truncate">
                {{ authStore.user?.email }}
              </p>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
          <RouterLink
            v-for="item in menuItems"
            :key="item.route"
            :to="{ name: item.route }"
            :class="[
              'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200',
              currentRouteName === item.route
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-gray-600 hover:bg-gray-100'
            ]"
            @click="closeSidebar"
          >
            <component :is="item.icon" class="w-5 h-5" />
            <span>{{ item.name }}</span>
          </RouterLink>
        </nav>

        <!-- Logout -->
        <div class="p-4 border-t border-gray-200">
          <button
            @click="handleLogout"
            class="flex items-center gap-3 w-full px-4 py-3 text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors duration-200"
          >
            <ArrowLeftOnRectangleIcon class="w-5 h-5" />
            <span>Sair</span>
          </button>
        </div>
      </div>
    </aside>

    <!-- Main content -->
    <div class="flex-1 min-w-0">
      <!-- Top bar -->
      <header class="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8">
        <button
          class="lg:hidden p-2 text-gray-500 hover:text-gray-700"
          @click="toggleSidebar"
        >
          <Bars3Icon class="w-6 h-6" />
        </button>

        <div class="flex items-center gap-4">
          <RouterLink
            to="/"
            class="text-sm text-gray-500 hover:text-primary-600 transition-colors"
          >
            ← Voltar ao site
          </RouterLink>
        </div>
      </header>

      <!-- Page content -->
      <main class="p-4 lg:p-8">
        <RouterView />
      </main>
    </div>
  </div>
</template>
